import React from 'react'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import ImageIcon from '@mui/icons-material/Image'
import VideocamIcon from '@mui/icons-material/Videocam'
import TableChartIcon from '@mui/icons-material/TableChart'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import TitleIcon from '@mui/icons-material/Title'
import ListAltIcon from '@mui/icons-material/ListAlt'
import SpeedIcon from '@mui/icons-material/Speed'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import WidgetsIcon from '@mui/icons-material/Widgets'
import SpaceBarIcon from '@mui/icons-material/SpaceBar'
import { DashboardCell } from '../../types/gotypes'

/**
 * Widget taxonomy (issue #966) — the single source of truth for widget type
 * names, categories, order, and one-line subtitles. Shared by the Edit Cell
 * modal (`CellEditModal`) and the layout builder's widget palette (#971) so
 * the two pickers never drift.
 *
 * Group order is deliberate (#1185): Text -> Data Display -> Layout. The
 * palette offers exactly six widgets — Title, Text Box, Gen AI Insights,
 * Chart, Table, and Spacer (#1187 — empty space, requested by Matt after the
 * row-height drag went away in #1177). Everything else stays defined here (so existing dashboards keep their
 * labels, ghosts, and renderers) but is hidden from the pickers by the
 * per-widget flags below — see `visibleWidgetGroups`.
 *
 * `type` is the *sidebar* type — 'chart' and 'table' both map onto the
 * underlying `table_graph` widget_type with a different `view_mode`; see
 * `CellEditModal.handleWidgetTypeChange` for that mapping.
 */
export interface WidgetTypeOption {
  type: string
  label: string
  subtitle: string
  icon: React.ReactNode
}

export interface WidgetGroup {
  label: string
  widgets: WidgetTypeOption[]
}

export const WIDGET_GROUPS: WidgetGroup[] = [
  {
    label: 'Text',
    widgets: [
      { type: 'title_hero', label: 'Title', subtitle: 'Section heading or hero banner', icon: <TitleIcon /> },
      { type: 'text', label: 'Text Box', subtitle: 'Free-form rich text you write', icon: <TextFieldsIcon /> },
      // #1185: the AI Analysis widget lives at the bottom of Text (it produces
      // written analysis), not in a group of its own.
      { type: 'multi_table_analysis', label: 'Gen AI Insights', subtitle: 'AI-written text linked to your tables', icon: <AnalyticsIcon /> },
      // Hidden behind AGENT_ARTIFACTS_WIDGET_ENABLED; would surface here if
      // ever re-enabled (its old "AI Analysis" group is gone).
      { type: 'agent_artifacts', label: 'Agent Artifacts', subtitle: 'Embed outputs from an agent session', icon: <SmartToyIcon /> },
    ],
  },
  {
    label: 'Data Display',
    widgets: [
      { type: 'chart', label: 'Chart', subtitle: 'Visualise a table as a chart', icon: <ShowChartIcon /> },
      { type: 'table', label: 'Table', subtitle: 'Show a crosstab table', icon: <TableChartIcon /> },
    ],
  },
  {
    // #1187: layout-only widgets. The Spacer renders nothing in view mode —
    // it exists to give a dashboard vertical or horizontal breathing room now
    // that row height comes purely from content (#1177).
    label: 'Layout',
    widgets: [
      { type: 'spacer', label: 'Spacer', subtitle: 'Empty space between widgets', icon: <SpaceBarIcon /> },
    ],
  },
  {
    // Every widget in this group is hidden by a flag below (#1185, #1180), so
    // the group never renders today. The definitions stay: WIDGET_TYPE_OPTIONS
    // and the drag ghosts still need them, and existing dashboards containing
    // these widgets keep rendering. Flip a flag to bring one back.
    label: 'Other',
    widgets: [
      { type: 'image', label: 'Image', subtitle: 'Upload or embed an image', icon: <ImageIcon /> },
      { type: 'video', label: 'Video', subtitle: 'Embed a video', icon: <VideocamIcon /> },
      { type: 'callout', label: 'Callout', subtitle: 'Highlighted card with icon and body', icon: <LightbulbIcon /> },
      { type: 'kpi_card', label: 'KPI Card', subtitle: 'Single headline metric', icon: <SpeedIcon /> },
      { type: 'toc', label: 'Table of Contents', subtitle: 'Auto-linked page navigation', icon: <ListAltIcon /> },
    ],
  },
]

/** Flat lookup by sidebar type, e.g. for the builder palette and placeholders. */
export const WIDGET_TYPE_OPTIONS: Record<string, WidgetTypeOption> = WIDGET_GROUPS.reduce(
  (acc, group) => {
    for (const widget of group.widgets) acc[widget.type] = widget
    return acc
  },
  {} as Record<string, WidgetTypeOption>
)

/**
 * The palette card to show as a DragOverlay ghost while an existing widget is
 * dragged around the canvas (#1130). Never returns undefined: the map above is
 * keyed by *sidebar* type, so indexing it with a cell's `widget_type` misses
 * `table_graph` — every chart and table on a real dashboard — and the drag ran
 * with no ghost at all, which is how drops ended up nowhere near a zone.
 */
export function dragGhostOption(cell: DashboardCell): WidgetTypeOption {
  const direct = WIDGET_TYPE_OPTIONS[cell.widget_type]
  if (direct) return direct
  if (cell.widget_type === 'table_graph') {
    return WIDGET_TYPE_OPTIONS[cell.table_graph_data?.view_mode === 'table' ? 'table' : 'chart']
  }
  if (cell.widget_type === 'layout') {
    return { type: 'layout', label: 'Column', subtitle: 'Column of widgets', icon: <ViewColumnIcon /> }
  }
  if (cell.widget_type === 'report_pillar') {
    return { type: 'report_pillar', label: 'Report Pillar', subtitle: 'Heading with Gen AI cards', icon: <ViewColumnIcon /> }
  }
  return { type: cell.widget_type, label: 'Widget', subtitle: '', icon: <WidgetsIcon /> }
}

/**
 * The dragged widget's own title, for the canvas-drag ghost (#1129). Each
 * widget type keeps its title in its own data blob, so this is the one place
 * that knows where to look. Returns undefined for an untitled widget (or a
 * whitespace-only title) — the ghost then falls back to the type label from
 * `dragGhostOption`, never an empty card.
 */
export function dragGhostTitle(cell: DashboardCell): string | undefined {
  const raw = (() => {
    switch (cell.widget_type) {
      case 'table_graph':
        return cell.table_graph_data?.title
      case 'multi_table_analysis':
        return cell.multi_table_analysis_data?.title
      case 'title_hero':
        return cell.title_hero_data?.heading
      case 'callout':
        return cell.callout_data?.title
      case 'kpi_card':
        return cell.kpi_card_data?.label
      case 'agent_artifacts':
        return cell.agent_artifact_data?.title
      case 'report_pillar':
        return cell.report_pillar_data?.heading
      default:
        return undefined
    }
  })()
  const title = raw?.trim()
  return title ? title : undefined
}

/**
 * (#966) `agent_artifacts` is hidden from the widget-type picker entirely
 * for now — single flag, flip it to re-enable. Dashboards that already
 * contain an agent_artifacts widget keep rendering it; this only controls
 * whether it's offered as a new choice.
 */
export const AGENT_ARTIFACTS_WIDGET_ENABLED = false

/**
 * (#1185, #1180) Per-widget visibility flags, one line each to re-enable —
 * same contract as `AGENT_ARTIFACTS_WIDGET_ENABLED` above. A `false` flag
 * hides the widget from the widget-type pickers entirely; dashboards that
 * already contain one keep rendering it. Nothing is deleted: the widget
 * types, data structures, renderers, and upload routes all remain.
 *
 * The known re-enable case is campaign analysis (#1180), where a researcher
 * wants the campaign image or video on screen beside the results — flip
 * IMAGE_WIDGET_ENABLED / VIDEO_WIDGET_ENABLED for that.
 */
export const IMAGE_WIDGET_ENABLED = false
export const VIDEO_WIDGET_ENABLED = false
export const CALLOUT_WIDGET_ENABLED = false
export const KPI_CARD_WIDGET_ENABLED = false
export const TOC_WIDGET_ENABLED = false

const WIDGET_TYPE_ENABLED: Record<string, boolean> = {
  image: IMAGE_WIDGET_ENABLED,
  video: VIDEO_WIDGET_ENABLED,
  callout: CALLOUT_WIDGET_ENABLED,
  kpi_card: KPI_CARD_WIDGET_ENABLED,
  toc: TOC_WIDGET_ENABLED,
}

/**
 * The groups the widget-type pickers actually offer. Applies the per-widget
 * flags above and drops any group left empty (which is how the Other group
 * disappears). `agent_artifacts` is additionally manager-only everywhere
 * it's offered as a choice.
 */
export function visibleWidgetGroups(isManager?: boolean): WidgetGroup[] {
  return WIDGET_GROUPS.map(group => ({
    ...group,
    widgets: group.widgets.filter(w => {
      if (w.type === 'agent_artifacts') return AGENT_ARTIFACTS_WIDGET_ENABLED && !!isManager
      return WIDGET_TYPE_ENABLED[w.type] !== false
    }),
  })).filter(g => g.widgets.length > 0)
}
